import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Filter } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export type TextCase = 'uppercase' | 'lowercase' | 'sentence' | 'original';

interface FilterDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  options: string[];
  selectedOptions: string[];
  onOptionToggle: (option: string) => void;
  onClear: () => void;
  onApply: () => void;
  getOptionLabel?: (option: string) => string;
  textCase?: TextCase;
}

const formatText = (text: string, textCase: TextCase = 'original'): string => {
  switch (textCase) {
    case 'uppercase':
      return text.toUpperCase();
    case 'lowercase':
      return text.toLowerCase();
    case 'sentence':
      return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    case 'original':
    default:
      return text;
  }
};

export const FilterDialog: React.FC<FilterDialogProps> = ({
  isOpen,
  onClose,
  title,
  searchValue,
  onSearchChange,
  options,
  selectedOptions,
  onOptionToggle,
  onClear,
  onApply,
  getOptionLabel = (option) => option,
  textCase = 'original',
}) => {
  const { t } = useTranslation();

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-30 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <Dialog.Title className="text-lg font-semibold mb-2 flex items-center gap-2 text-brand-orange">
                <Filter className="w-5 h-5 text-brand-orange" />
                {title}
              </Dialog.Title>
              <input
                type="text"
                className="w-full border border-gray-300 rounded px-3 py-2 mb-3"
                placeholder={t('pages.users.dialog.search')}
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
              />
              <div className="max-h-48 overflow-y-auto mb-4 border rounded p-2">
                {options.map((opt) => (
                  <label key={opt} className="flex items-center gap-2 py-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedOptions.includes(opt)}
                      onChange={() => onOptionToggle(opt)}
                      className="accent-brand-orange w-4 h-4 border-2 border-brand-orange focus:ring-brand-orange"
                    />
                    <span className="text-black">{formatText(getOptionLabel(opt), textCase)}</span>
                  </label>
                ))}
                {options.length === 0 && (
                  <div className="text-gray-400 text-sm text-center py-2">
                    {t('pages.users.dialog.noResults')}
                  </div>
                )}
              </div>
              <div className="flex justify-between gap-2">
                <button
                  className="px-3 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
                  onClick={onClose}
                >
                  {t('pages.users.dialog.close')}
                </button>
                <div className="flex gap-2">
                  <button
                    className="px-3 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
                    onClick={onClear}
                  >
                    {t('pages.users.dialog.clear')}
                  </button>
                  <button
                    className="px-3 py-1 rounded bg-brand-orange text-white hover:bg-brand-orange-dark"
                    onClick={onApply}
                  >
                    {t('pages.users.dialog.apply')}
                  </button>
                </div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
